import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import { useB2B } from "@/context/B2BContext";

const CartContext = createContext(null);
const STORAGE_KEY = "levami_cart_v1";

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const { currency } = useLocale();
  const { account, loading: b2bLoading } = useB2B();
  const [items, setItems] = useState(loadCart);
  const [pricing, setPricing] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pricingError, setPricingError] = useState("");
  const [shippingCountry, setShippingCountry] = useState("FR");
  const pricingRequest = useRef(0);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const refreshPricing = useCallback(async (code) => {
    const requestId = ++pricingRequest.current;
    if (account || b2bLoading) {
      setPricing(null);
      setLoading(false);
      setPricingError("");
      return;
    }
    if (items.length === 0) {
      setPricing(null);
      setLoading(false);
      setPricingError("");
      return;
    }
    setLoading(true);
    setPricingError("");
    const selectedCode = String(code !== undefined ? code : couponCode || "").trim();
    const body = {
      items: items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity })),
      coupon_code: selectedCode || null, customer: {}, shipping_address: { country: shippingCountry }, currency,
    };
    try {
      const res = await api.post("/checkout/price", body);
      if (requestId !== pricingRequest.current) return;
      setPricing(res.data);
      setCouponError("");
      setCouponCode(res.data.coupon_code || "");
    } catch (err) {
      if (requestId !== pricingRequest.current) return;
      if (selectedCode && err.response?.status === 400) {
        try {
          const { data } = await api.post("/checkout/price", { ...body, coupon_code: null });
          if (requestId !== pricingRequest.current) return;
          setPricing(data); setCouponCode(""); setCouponError(getErrorMessage(err));
        } catch (fallbackError) {
          if (requestId !== pricingRequest.current) return;
          setPricing(null); setPricingError(getErrorMessage(fallbackError));
        }
      } else {
        setPricing(null); setPricingError(getErrorMessage(err));
      }
    } finally {
      if (requestId === pricingRequest.current) setLoading(false);
    }
  }, [items, couponCode, currency, account, b2bLoading, shippingCountry]);

  useEffect(() => {
    refreshPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, JSON.stringify(items.map((i) => [i.variant_id, i.quantity])), currency, account, b2bLoading, shippingCountry]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.variant_id === item.variant_id);
      if (existing) {
        return prev.map((i) => (i.variant_id === item.variant_id ? { ...i, quantity: i.quantity + item.quantity } : i));
      }
      return [...prev, item];
    });
    setIsOpen(true);
  }, []);

  const updateQuantity = useCallback((variantId, quantity) => {
    setItems((prev) => prev.map((i) => (i.variant_id === variantId ? { ...i, quantity: Math.max(1, quantity) } : i)));
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems((prev) => prev.filter((i) => i.variant_id !== variantId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setPricing(null);
    setCouponCode("");
  }, []);

  const applyCoupon = useCallback(async (code) => {
    await refreshPricing(code);
  }, [refreshPricing]);

  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

  const value = {
    items, addItem, updateQuantity, removeItem, clearCart,
    pricing, loading, isOpen, setIsOpen, itemCount,
    couponCode, applyCoupon, couponError, refreshPricing, pricingError, shippingCountry, setShippingCountry,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
